import type { ReportPatterns } from '../../lib/api';

/** A page key without its host: what a person calls the page. */
export const pathOf = (page: string) => {
  const slash = page.indexOf('/');
  return slash < 0 ? '/' : page.slice(slash);
};
const hostOf = (page: string) => {
  const slash = page.indexOf('/');
  return slash < 0 ? page : page.slice(0, slash);
};

/**
 * How the report names a page: by its path alone while every page in it is on
 * one host, and with the host once there are two, so `/settings` on the shop
 * and `/settings` on the admin site never read as one page (Codex review
 * round 10).
 */
export function pageNamer(report: ReportPatterns): (page: string) => string {
  const hosts = new Set([
    ...report.sameElement.map((entry) => hostOf(entry.page)),
    ...report.rhythm.map((entry) => hostOf(entry.page)),
    ...report.areas.flatMap((area) => [hostOf(area.area), ...area.pages.map((page) => hostOf(page.page))]),
    ...report.reopens.byPage.flatMap((entry) => (entry.page ? [hostOf(entry.page)] : [])),
  ]);
  return hosts.size > 1 ? (page) => page : pathOf;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
/** A sentence after another: starts with a capital. */
const then = (text: string) => ` ${text.charAt(0).toUpperCase()}${text.slice(1)}`;

/** How an element is named in a sentence: by its test id, its component, or its tag and text. */
export function elementName(element: ReportPatterns['sameElement'][number]['element']): string {
  if (element.kind === 'testId') return `The element "${element.label}"`;
  if (element.kind === 'component') return `The ${element.label} component`;
  return `The ${element.label}`;
}

export type Finding = { key: 'element' | 'chronic' | 'reopens'; text: string; tickets: Array<{ id: string; title: string }> };

/**
 * The Patterns card's three findings in plain words (TOADO-862), computed
 * from the report, never a model's prose: the element tickets keep landing
 * on, the page that keeps failing, and the fixes that did not stick. Each
 * names the tickets behind it.
 *
 * One rule for what a number claims (Codex review rounds 11 to 13): a list
 * answered in part is summed from the report's `counts`, never from its
 * length; when the tickets were read short (`truncated`), every figure drawn
 * from them can only be an undercount, so it reads "at least", and finding
 * nothing proves nothing, so absence is not claimed. Reopens are counted in
 * full on the server; only their ranking by page is sampled when short.
 */
export function patternFindings(report: ReportPatterns): Finding[] {
  const name = pageNamer(report);
  const atLeast = report.truncated ? 'at least ' : '';
  const top = report.sameElement[0];
  const others = report.counts.sameElement - 1;
  const element: Finding = top
    ? {
      key: 'element',
      text: `${elementName(top.element)} on ${name(top.page)}: ${atLeast}${plural(top.tickets, 'ticket')}, ${atLeast}${plural(top.people, 'person', 'people')}.`
        + (others > 0 ? then(`${atLeast}${plural(others, 'other element')} drew three or more.`) : ''),
      tickets: top.examples,
    }
    : report.truncated
      ? { key: 'element', text: 'Too many tickets in this period to say whether one element keeps drawing them. Choose one project or a shorter period.', tickets: [] }
      : { key: 'element', text: 'No element drew three or more tickets in this period.', tickets: [] };

  const first = report.rhythm.find((entry) => entry.rhythm === 'chronic');
  const more = report.counts.chronic - 1;
  const chronicFinding: Finding = first
    ? {
      key: 'chronic',
      text: `${name(first.page)} keeps coming back: tickets in ${atLeast}${first.weeks} of the last 8 weeks.`
        + (more > 0 ? then(`${atLeast}${plural(more, 'more page')} ${more === 1 ? 'is' : 'are'} chronic.`) : ''),
      tickets: first.examples,
    }
    : report.truncated
      ? { key: 'chronic', text: 'Too many tickets in the last 8 weeks to say which pages keep coming back. Choose one project.', tickets: [] }
      : { key: 'chronic', text: 'No page had tickets in three or more of the last 8 weeks.', tickets: [] };

  const worst = report.reopens.byPage[0];
  const where = !worst || report.reopens.truncated ? '.' : worst.page ? `, most on ${name(worst.page)}.` : '.';
  const reopens: Finding = report.reopens.total > 0
    ? {
      key: 'reopens',
      text: `${plural(report.reopens.total, 'fix', 'fixes')} didn't stick: reopened after reaching the last column${where}`,
      tickets: worst?.tickets ?? [],
    }
    : { key: 'reopens', text: 'No reopens recorded in this period.', tickets: [] };

  return [element, chronicFinding, reopens];
}
